import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NgbAlert } from '@ng-bootstrap/ng-bootstrap';

import { InputFieldComponent } from '../../../../components/form/input-field/input-field.component';
import { SelectFieldComponent } from '../../../../components/form/select-field/select-field.component';
import { TextAreaFieldComponent } from '../../../../components/form/textarea-field/textarea-field.component';
import { SelectOption } from '../../../../components/form/select-field/select-field.config';
import { MeetingMinuteParticipantsComponent } from '../meeting-minute-participants/meeting-minute-participants.component';
import { MeetingMinuteActionsComponent } from '../meeting-minute-actions/meeting-minute-actions.component';
import { MeetingMinuteAgreementsComponent } from '../meeting-minute-agreements/meeting-minute-agreements.component';
import { MeetingMinuteRisksComponent } from '../meeting-minute-risks/meeting-minute-risks.component';
import { MeetingMinuteNextMeetingsComponent } from '../meeting-minute-next-meetings/meeting-minute-next-meetings.component';

import { QualityManagementService } from '../../../../services/quality-management.service';
import { OptionService } from '../../../../services/api/option.service';
import { LoaderService } from '../../../../services/loader.service';
import { AlertNotificationService } from '../../../../services/alert-notification.service';
import { MainLayoutService } from '../../../../services/main-layout.service';
import { AccessControlService } from '../../../../services/access-control.service';

import { AlertInfo } from '../../../../models/alert-info';

import {
  MeetingMinute,
  MeetingMinuteAction,
  MeetingMinuteAgreement,
  MeetingMinuteNext,
  MeetingMinuteParticipant,
  MeetingMinutePayload,
  MeetingMinuteRisk
} from '../../../../models/entities/meeting-minute';

const EMPTY_OPTION: SelectOption = { key: '', value: '-- select' };

@Component({
  selector: 'meeting-minute-form',
  imports: [
    ReactiveFormsModule,
    NgbAlert,
    InputFieldComponent,
    SelectFieldComponent,
    TextAreaFieldComponent,
    MeetingMinuteParticipantsComponent,
    MeetingMinuteActionsComponent,
    MeetingMinuteAgreementsComponent,
    MeetingMinuteRisksComponent,
    MeetingMinuteNextMeetingsComponent
  ],
  templateUrl: './meeting-minute-form.component.html',
  styleUrl: './meeting-minute-form.component.scss'
})
export class MeetingMinuteFormComponent implements OnInit, OnChanges, OnDestroy {

  @Input() meetingMinute?: MeetingMinute;
  @Input() meetingMinuteId?: number;
  @Input() readonlyForm: boolean = false;
  @Input() loaderContext: string = 'meeting-minute-form';

  @Output() onSavedData = new EventEmitter<MeetingMinute>();

  form: FormGroup;
  departmentOptions: SelectOption[] = [EMPTY_OPTION];
  participantStatusOptions: SelectOption[] = [EMPTY_OPTION];
  userOptions: SelectOption[] = [EMPTY_OPTION];

  canModify: boolean = false;
  alertInfo: AlertInfo = { show: false, type: '', message: '' };

  private alertTimeout: any = null;
  private destroy$ = new Subject<void>();

  constructor(
    private formBuilder: FormBuilder,
    private qualityManagementService: QualityManagementService,
    private optionService: OptionService,
    private loaderService: LoaderService,
    private alertNotificationService: AlertNotificationService,
    private mainLayoutService: MainLayoutService,
    private accessControlService: AccessControlService
  ) {
    this.form = this.formBuilder.group({
      subject: ['', [Validators.required]],
      date: ['', [Validators.required]],
      startTime: ['', [Validators.required]],
      endTime: ['', [Validators.required]],
      place: ['', [Validators.required]],
      writtenByUserId: ['', [Validators.required]],
      reviewedByUserId: ['', [Validators.required]],
      proposedTopic: ['', [Validators.required]],
      developmentOfTopic: ['', [Validators.required]],
      department: ['', [Validators.required]],
      reminder: ['', [Validators.required]],
      participants: this.formBuilder.array<FormGroup>([]),
      actions: this.formBuilder.array<FormGroup>([]),
      agreements: this.formBuilder.array<FormGroup>([]),
      risks: this.formBuilder.array<FormGroup>([]),
      nextMeetings: this.formBuilder.array<FormGroup>([])
    });
  }

  ngOnInit(): void {
    this.mainLayoutService.activeProfile$
      .pipe(takeUntil(this.destroy$))
      .subscribe(profileCode => {
        this.canModify = this.accessControlService.hasFeatureActionAccess(
          profileCode,
          'QUALITY_MANAGEMENT_MEETING_MINUTES',
          'modify'
        );
        this.applyFieldPermissions();
      });

    this.loadOptions();
    this.applyMeetingMinute();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['meetingMinute'] || changes['readonlyForm']) {
      this.applyMeetingMinute();
    }
  }

  ngOnDestroy(): void {
    this.loaderService.stopLoading(this.loaderContext);
    this.clearAlertTimeout();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get participants(): FormArray<FormGroup> {
    return this.form.get('participants') as FormArray<FormGroup>;
  }

  get actions(): FormArray<FormGroup> {
    return this.form.get('actions') as FormArray<FormGroup>;
  }

  get agreements(): FormArray<FormGroup> {
    return this.form.get('agreements') as FormArray<FormGroup>;
  }

  get risks(): FormArray<FormGroup> {
    return this.form.get('risks') as FormArray<FormGroup>;
  }

  get nextMeetings(): FormArray<FormGroup> {
    return this.form.get('nextMeetings') as FormArray<FormGroup>;
  }

  get canSave(): boolean {
    return this.canModify && !this.readonlyForm;
  }

  getControl(name: string): FormControl {
    return this.form.get(name) as FormControl;
  }

  addParticipant(): void {
    this.participants.push(this.buildParticipantGroup());
  }

  removeParticipant(index: number): void {
    this.participants.removeAt(index);
  }

  addAction(): void {
    this.actions.push(this.buildActionGroup());
  }

  removeAction(index: number): void {
    this.actions.removeAt(index);
  }

  addAgreement(): void {
    this.agreements.push(this.buildAgreementGroup());
  }

  removeAgreement(index: number): void {
    this.agreements.removeAt(index);
  }

  addRisk(): void {
    this.risks.push(this.buildRiskGroup());
  }

  removeRisk(index: number): void {
    this.risks.removeAt(index);
  }

  addNextMeeting(): void {
    this.nextMeetings.push(this.buildNextMeetingGroup());
  }

  removeNextMeeting(index: number): void {
    this.nextMeetings.removeAt(index);
  }

  submitForm(): void {
    if (!this.canSave) {
      return;
    }

    this.alertInfo.show = false;

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.showAlertInfo('danger', 'Please fill in all the required fields.');
      return;
    }

    const payload = this.buildPayload();

    const request$ = this.meetingMinuteId
      ? this.qualityManagementService.updateMeetingMinute(this.meetingMinuteId, payload)
      : this.qualityManagementService.createMeetingMinute(payload);

    this.loaderService.startLoading(this.loaderContext);

    request$.subscribe({
      next: (response: any) => {
        this.loaderService.stopLoading(this.loaderContext);

        const meetingMinute: MeetingMinute | null =
          this.qualityManagementService.readResponseKey(response, 'meetingMinute') ?? null;

        if (!meetingMinute) {
          this.applyBackendErrors(response);
          return;
        }

        this.meetingMinute = meetingMinute;
        this.meetingMinuteId = meetingMinute.meetingMinuteId ?? this.meetingMinuteId;
        this.applyMeetingMinute();
        this.alertNotificationService.success('Meeting minute saved.');
        this.showAlertInfo('success', 'Meeting minute saved.');
        this.onSavedData.emit(meetingMinute);
      },
      error: (error: any) => {
        console.error('Error saving the meeting minute:', error);
        this.loaderService.stopLoading(this.loaderContext);
        this.applyBackendErrors(error?.error, error);
      }
    });
  }

  private buildParticipantGroup(participant?: MeetingMinuteParticipant): FormGroup {
    return this.formBuilder.group({
      meetingMinuteParticipantId: [participant?.meetingMinuteParticipantId ?? null],
      userId: [participant?.user?.userId ?? '', [Validators.required]],
      status: [participant?.status ?? '', [Validators.required]]
    });
  }

  private buildActionGroup(action?: MeetingMinuteAction): FormGroup {
    return this.formBuilder.group({
      meetingMinuteActionId: [action?.meetingMinuteActionId ?? null],
      task: [action?.task ?? '', [Validators.required]],
      responsibleUserId: [action?.responsibleUser?.userId ?? '', [Validators.required]],
      date: [this.qualityManagementService.toDateInput(action?.date), [Validators.required]],
      comment: [action?.comment ?? '', [Validators.required]]
    });
  }

  private buildAgreementGroup(agreement?: MeetingMinuteAgreement): FormGroup {
    return this.formBuilder.group({
      meetingMinuteAgreementId: [agreement?.meetingMinuteAgreementId ?? null],
      date: [this.qualityManagementService.toDateInput(agreement?.date), [Validators.required]],
      comment: [agreement?.comment ?? '', [Validators.required]]
    });
  }

  private buildRiskGroup(risk?: MeetingMinuteRisk): FormGroup {
    return this.formBuilder.group({
      meetingMinuteRiskId: [risk?.meetingMinuteRiskId ?? null],
      riskDetected: [risk?.riskDetected ?? '', [Validators.required]],
      description: [risk?.description ?? '', [Validators.required]],
      reportedByUserId: [risk?.reportedByUser?.userId ?? '', [Validators.required]],
      reportedDate: [this.qualityManagementService.toDateInput(risk?.reportedDate), [Validators.required]],
      assignToUserId: [risk?.assignToUser?.userId ?? '', [Validators.required]],
      action: [risk?.action ?? '']
    });
  }

  private buildNextMeetingGroup(nextMeeting?: MeetingMinuteNext): FormGroup {
    return this.formBuilder.group({
      meetingMinuteNextId: [nextMeeting?.meetingMinuteNextId ?? null],
      topics: [nextMeeting?.topics ?? '', [Validators.required]],
      date: [this.qualityManagementService.toDateTimeLocal(nextMeeting?.date), [Validators.required]],
      place: [nextMeeting?.place ?? '', [Validators.required]]
    });
  }

  private buildPayload(): MeetingMinutePayload {
    const value = this.form.getRawValue();
    const service = this.qualityManagementService;

    return {
      subject: value.subject,
      date: service.fromDateInput(value.date),
      startTime: service.fromTimeInput(value.startTime),
      endTime: service.fromTimeInput(value.endTime),
      place: value.place,
      writtenByUserId: Number(value.writtenByUserId),
      reviewedByUserId: Number(value.reviewedByUserId),
      proposedTopic: value.proposedTopic,
      developmentOfTopic: value.developmentOfTopic,
      department: value.department,
      reminder: service.fromDateInput(value.reminder),
      participants: (value.participants ?? []).map((row: any) => ({
        meetingMinuteParticipantId: row.meetingMinuteParticipantId ?? null,
        userId: Number(row.userId),
        status: row.status
      })),
      actions: (value.actions ?? []).map((row: any) => ({
        meetingMinuteActionId: row.meetingMinuteActionId ?? null,
        task: row.task,
        responsibleUserId: Number(row.responsibleUserId),
        date: service.fromDateInput(row.date),
        comment: row.comment
      })),
      agreements: (value.agreements ?? []).map((row: any) => ({
        meetingMinuteAgreementId: row.meetingMinuteAgreementId ?? null,
        date: service.fromDateInput(row.date),
        comment: row.comment
      })),
      risks: (value.risks ?? []).map((row: any) => ({
        meetingMinuteRiskId: row.meetingMinuteRiskId ?? null,
        riskDetected: row.riskDetected,
        description: row.description,
        reportedByUserId: Number(row.reportedByUserId),
        reportedDate: service.fromDateInput(row.reportedDate),
        assignToUserId: Number(row.assignToUserId),
        action: row.action ? row.action : null
      })),
      nextMeetings: (value.nextMeetings ?? []).map((row: any) => ({
        meetingMinuteNextId: row.meetingMinuteNextId ?? null,
        topics: row.topics,
        date: service.fromDateTimeLocal(row.date),
        place: row.place
      }))
    };
  }

  private loadOptions(): void {
    this.loadOptionList(this.optionService.meetingMinuteDepartments(), 'departmentOptions', 'the departments list');
    this.loadOptionList(
      this.optionService.meetingMinuteParticipantStatuses(),
      'participantStatusOptions',
      'the participant statuses list'
    );
    this.loadOptionList(this.optionService.meetingMinuteUsers(), 'userOptions', 'the users list');
  }

  private loadOptionList(source$: any, target: string, label: string): void {
    source$.subscribe({
      next: (response: any) => {
        (this as any)[target] = [EMPTY_OPTION, ...(response?.data?.options ?? [])];
      },
      error: (error: any) => {
        console.error(`Error loading ${label}:`, error);
        this.alertNotificationService.error(`Failed to load ${label}.`);
      }
    });
  }

  private applyMeetingMinute(): void {
    if (this.meetingMinute) {
      const meetingMinute = this.meetingMinute;
      const service = this.qualityManagementService;

      this.meetingMinuteId = meetingMinute.meetingMinuteId ?? this.meetingMinuteId;

      this.form.patchValue({
        subject: meetingMinute.subject ?? '',
        date: service.toDateInput(meetingMinute.date),
        startTime: service.toTimeInput(meetingMinute.startTime),
        endTime: service.toTimeInput(meetingMinute.endTime),
        place: meetingMinute.place ?? '',
        writtenByUserId: meetingMinute.writtenByUser?.userId ?? '',
        reviewedByUserId: meetingMinute.reviewedByUser?.userId ?? '',
        proposedTopic: meetingMinute.proposedTopic ?? '',
        developmentOfTopic: meetingMinute.developmentOfTopic ?? '',
        department: meetingMinute.department ?? '',
        reminder: service.toDateInput(meetingMinute.reminder)
      }, { emitEvent: false });

      this.replaceRows(this.participants, meetingMinute.participants, row => this.buildParticipantGroup(row));
      this.replaceRows(this.actions, meetingMinute.actions, row => this.buildActionGroup(row));
      this.replaceRows(this.agreements, meetingMinute.agreements, row => this.buildAgreementGroup(row));
      this.replaceRows(this.risks, meetingMinute.risks, row => this.buildRiskGroup(row));
      this.replaceRows(this.nextMeetings, meetingMinute.nextMeetings, row => this.buildNextMeetingGroup(row));
    }

    this.applyFieldPermissions();
  }

  private replaceRows<T>(
    formArray: FormArray<FormGroup>,
    items: T[] | null | undefined,
    build: (item: T) => FormGroup
  ): void {
    formArray.clear({ emitEvent: false });
    (items ?? []).forEach(item => formArray.push(build(item), { emitEvent: false }));
  }

  private applyFieldPermissions(): void {
    if (!this.canSave) {
      this.form.disable({ emitEvent: false });
      return;
    }

    this.form.enable({ emitEvent: false });
  }

  private applyBackendErrors(response: any, error?: any): void {
    const validationErrors = response?.error?.validationErrors
      ?? response?.validationErrors
      ?? response?.errors
      ?? null;

    const messagesByField = this.normalizeValidationErrors(validationErrors);

    if (messagesByField) {
      Object.keys(messagesByField).forEach(field => {
        const control = this.resolveControl(field);

        if (control) {
          control.markAsTouched();
          control.setErrors({
            ...(control.errors ?? {}),
            backendError: messagesByField[field],
            back: messagesByField[field]
          });
        }
      });

      this.showAlertInfo('danger', Object.values(messagesByField).join(' '));
      return;
    }

    this.showAlertInfo(
      'danger',
      this.qualityManagementService.meetingMinuteErrorMessage(
        error ?? { error: response },
        'Failed to save the meeting minute.'
      )
    );
  }

  private showAlertInfo(type: string, message: string): void {
    this.clearAlertTimeout();
    this.alertInfo = { show: true, type, message };
    window.scrollTo({ top: 0, behavior: 'smooth' });
    this.alertTimeout = setTimeout(() => this.alertInfo.show = false, 3000);
  }

  private clearAlertTimeout(): void {
    if (this.alertTimeout) {
      clearTimeout(this.alertTimeout);
      this.alertTimeout = null;
    }
  }

  private normalizeValidationErrors(validationErrors: any): Record<string, string> | null {
    if (Array.isArray(validationErrors)) {
      const messagesByField: Record<string, string> = {};

      validationErrors
        .filter(entry => entry?.field != null)
        .forEach(entry => messagesByField[entry.field] = entry.defaultMessage);

      return Object.keys(messagesByField).length ? messagesByField : null;
    }

    if (validationErrors && typeof validationErrors === 'object') {
      return validationErrors as Record<string, string>;
    }

    return null;
  }

  private resolveControl(field: string): FormControl | null {
    const path = field.replace(/\[(\d+)\]/g, '.$1');
    return (this.form.get(path) as FormControl) ?? null;
  }
}
